import React, { useEffect } from 'react'
import { Modifier } from '@popperjs/core'

import { useResponsiveDropdown } from '../ResponsiveDropdown'
import { Truncate } from '../Truncate'
import { PersonAvatar } from '../PersonAvatar'
import { ListItem } from '../List'
import { strippedString } from './utils'
import { SuggestionItem, TriggerType } from './types'
import { MentionWrapper } from './SuggestionsInput.styles'

const POPOVER_MODIFIERS: Partial<Modifier<any, any>>[] = [
  {
    name: 'makeSameWidth',
    phase: 'beforeWrite' as const,
    enabled: true,
    fn({ state }) {
      const { popper, reference } = state.elements || {}

      if (!(reference && reference instanceof HTMLElement)) {
        return
      }

      const { width } = reference.getBoundingClientRect()
      popper.style.width = `${width}px`
    },
  },
  {
    name: 'computeStyles',
    options: { gpuAcceleration: true },
  },
]

const POPOVER_OFFSET = [0, 0] as [number, number]

const customHighlightDisplay = (display: string, query: string) => {
  const strippedDisplay = strippedString(display)
  const strippedQuery = strippedString(query)
  const i = strippedDisplay.indexOf(strippedQuery)

  if (i === -1) {
    return display
  }

  return (
    <>
      {display.substring(0, i)}
      <b>{display.substring(i, i + query.length)}</b>
      {display.substring(i + query.length)}
    </>
  )
}

interface SuggestionsPopoverContentProps {
  suggestions: SuggestionItem[]
  selectedIndex: number
  triggerType: TriggerType
  onSelect: (item: SuggestionItem) => void
  searchQuery: string
}

const SuggestionsPopoverContent: React.FC<SuggestionsPopoverContentProps> = ({
  suggestions,
  selectedIndex,
  triggerType,
  onSelect,
  searchQuery,
}) => {
  return (
    <MentionWrapper>
      {suggestions.map((item, index) => {
        const isSelected = index === selectedIndex
        const className = isSelected ? 'suggestion--focused' : ''

        if (triggerType === 'emoji') {
          return (
            <div
              key={item.id}
              className={className}
              onClick={() => onSelect(item)}
              role="option"
              aria-selected={isSelected}
            >
              <ListItem icon={props => <div {...props}>{item.display}</div>}>
                {item.id}
              </ListItem>
            </div>
          )
        }

        // User suggestion
        return (
          <div
            key={item.id}
            className={className}
            onClick={() => onSelect(item)}
            role="option"
            aria-selected={isSelected}
          >
            <ListItem
              icon={({ className }) => (
                <PersonAvatar
                  className={className}
                  size="17px"
                  name={item.display || ''}
                  flavour="image"
                  src={item.avatar || ''}
                />
              )}
            >
              <Truncate>
                {customHighlightDisplay(item.display || '', searchQuery)}
              </Truncate>
            </ListItem>
          </div>
        )
      })}
    </MentionWrapper>
  )
}

interface SuggestionsPopoverProps {
  suggestions: SuggestionItem[]
  selectedIndex: number
  triggerType: TriggerType
  onSelect: (item: SuggestionItem) => void
  onClose: () => void
  searchQuery: string
  anchorRef: React.RefObject<HTMLTextAreaElement | null>
}

export const SuggestionsPopover: React.FC<SuggestionsPopoverProps> = ({
  suggestions,
  selectedIndex,
  triggerType,
  onSelect,
  onClose,
  searchQuery,
  anchorRef,
}) => {
  const [content, , { setVisible, update }] = useResponsiveDropdown({
    dropdown: SuggestionsPopoverContent,
    dropdownProps: {
      suggestions,
      selectedIndex,
      triggerType,
      onSelect,
      searchQuery,
    },
    usePortal: true,
    placement: 'top-start',
    modifiers: POPOVER_MODIFIERS,
    dropdownBreakpoint: 'base',
    offset: POPOVER_OFFSET,
    hide: ['clickedOutsideContent'],
    onHide: onClose,
    triggerRef: anchorRef,
  })

  useEffect(() => {
    setVisible?.(true)
    update?.()
  }, [setVisible, update])

  return <>{content}</>
}
